import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  TEMPLATE_IDS,
  TEMPLATE_META,
} from "../../utils/emailTemplateLoader";
import { DEFAULT_TEMPLATES } from "../../utils/openHouseEmails";
import { interpolate } from "../../utils/emailPlaceholders";
import {
  buildBanner,
  buildSignature,
  buildFooter,
  wrapEmail,
  paragraphsToHtml,
} from "../../utils/openHouseEmailsHtml";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { normalizeContact } from "../../utils/contactNormalize";

// Templates eligible for manual send (i.e. not the auto-triggered OH ones).
const MANUAL_TEMPLATE_IDS = TEMPLATE_IDS.filter(
  (id) =>
    !TEMPLATE_META[id]?.auto &&
    TEMPLATE_META[id]?.category !== "Post-Acceptance",
);

function effective(templateId, customizations) {
  const d = DEFAULT_TEMPLATES[templateId] || {};
  const c = customizations || {};
  return {
    subject: c.subject ?? d.subject ?? "",
    body: c.body ?? d.body ?? "",
  };
}

export default function SendTemplateModal({ contact, onClose }) {
  const [{ user, emailTemplates }] = useStateValue();

  const normalized = useMemo(() => normalizeContact(contact), [contact]);

  const [templateId, setTemplateId] = useState(MANUAL_TEMPLATE_IDS[0]);
  const [yearsInHome, setYearsInHome] = useState("");
  const [propertyAddress, setPropertyAddress] = useState(
    normalized?.address?.street
      ? `${normalized.address.street}${normalized.address.city ? ", " + normalized.address.city : ""}`
      : "",
  );
  const [recipient, setRecipient] = useState(
    normalized?.email || "",
  );
  const [recipientName, setRecipientName] = useState(
    normalized?.name || "",
  );

  // Pull subject + body (from defaults or saved customization) and let the
  // user edit before sending.
  const saved = effective(templateId, emailTemplates?.[templateId]);
  const [subjectDraft, setSubjectDraft] = useState(saved.subject);
  const [bodyDraft, setBodyDraft] = useState(saved.body);
  const [sending, setSending] = useState(false);

  // When template switches, refill drafts.
  const onTemplateChange = (id) => {
    setTemplateId(id);
    const next = effective(id, emailTemplates?.[id]);
    setSubjectDraft(next.subject);
    setBodyDraft(next.body);
  };

  const data = useMemo(
    () => ({
      contactFirstName:
        normalized?.firstName ||
        (normalized?.name || "").split(/\s+/)[0] ||
        "",
      contactName: normalized?.name || "",
      propertyAddress: propertyAddress || "",
      yearsInHome: yearsInHome || "",
      senderFirstName: user?.firstname || "",
      senderName: `${user?.firstname || ""} ${user?.lastname || ""}`.trim(),
    }),
    [normalized, propertyAddress, yearsInHome, user],
  );

  const renderedSubject = useMemo(
    () => interpolate(subjectDraft, data),
    [subjectDraft, data],
  );
  const renderedBody = useMemo(
    () => interpolate(bodyDraft, data),
    [bodyDraft, data],
  );

  const html = useMemo(() => {
    try {
      const banner = buildBanner(
        { propertyAddress: data.propertyAddress },
        "",
      );
      const body = paragraphsToHtml(renderedBody);
      const signature = buildSignature(user);
      const footer = buildFooter({});
      return wrapEmail({ banner, body, signature, footer });
    } catch (err) {
      return `<pre>Preview error: ${err.message}</pre>`;
    }
  }, [data.propertyAddress, renderedBody, user]);

  const needsYears = templateId === "home-anniversary";
  const needsProperty = ["home-anniversary", "closing-day", "just-listed", "just-sold"].includes(
    templateId,
  );

  const handleSend = async () => {
    if (!recipient.trim()) {
      toast.error("Enter a recipient email.");
      return;
    }
    if (!renderedSubject.trim() || !renderedBody.trim()) {
      toast.error("Subject and body can't be empty.");
      return;
    }
    if (needsYears && !yearsInHome) {
      toast.error("Enter the number of years in home for this template.");
      return;
    }
    if (needsProperty && !propertyAddress.trim()) {
      toast.error("Enter the property address for this template.");
      return;
    }
    setSending(true);
    try {
      const res = await sendOpenHouseEmail({
        userId: user.id,
        to: recipient,
        toName: recipientName,
        subject: renderedSubject,
        text: renderedBody,
        html,
        type: templateId,
      });
      if (!res.ok) {
        toast.error(`Send failed: ${res.error || "unknown"}`);
        return;
      }
      toast.success("Email sent.");
      onClose?.();
    } catch (err) {
      console.error("Send failed:", err);
      toast.error("Send failed. Check your connection.");
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/55 z-[100]"
        onClick={sending ? undefined : onClose}
      />
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-indigo-600" />
              <h2 className="text-base font-semibold text-slate-900">
                Send template
              </h2>
            </div>
            <button
              onClick={onClose}
              disabled={sending}
              className="w-8 h-8 hover:bg-slate-100 rounded-full flex items-center justify-center disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left: form */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Template
                </label>
                <select
                  value={templateId}
                  onChange={(e) => onTemplateChange(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {MANUAL_TEMPLATE_IDS.map((id) => (
                    <option key={id} value={id}>
                      {TEMPLATE_META[id]?.label || id}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  {TEMPLATE_META[templateId]?.meta}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Recipient name
                  </label>
                  <input
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Recipient email
                  </label>
                  <input
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {needsProperty && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Property address
                  </label>
                  <input
                    value={propertyAddress}
                    onChange={(e) => setPropertyAddress(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              )}

              {needsYears && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Years in home
                  </label>
                  <input
                    type="number"
                    value={yearsInHome}
                    onChange={(e) => setYearsInHome(e.target.value)}
                    placeholder="e.g. 3"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Subject
                </label>
                <input
                  value={subjectDraft}
                  onChange={(e) => setSubjectDraft(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Body
                </label>
                <textarea
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  rows={10}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Placeholders like {"{{contactFirstName}}"} fill in
                  automatically.
                </p>
              </div>
            </div>

            {/* Right: preview */}
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Preview · how the recipient will see this
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50">
                <div className="px-3 py-2 border-b border-slate-200 bg-white text-[12px] text-slate-700">
                  <strong>Subject:</strong> {renderedSubject}
                </div>
                <iframe
                  title="email-preview"
                  srcDoc={html}
                  className="w-full h-[500px] bg-white"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
